import type { InferTranslation } from "typed-locale";
import { useCasesEn } from "./use-cases.en";

export const en = {
	locale: "en",
	seo: {
		blogTitle: "Blog",
		pages: {
			home: {
				name: "Home",
				title: "Web apps, mobile apps and business software",
				description:
					"Lezo builds and maintains web and mobile applications with supervised, AI-assisted development: custom business software for about a fifth of the usual cost, and React Native to Expo migration.",
			},
			businessApps: {
				name: "Business applications",
				title: "Custom business applications",
				description:
					"Custom business software built with AI under a developer’s control: about five times cheaper than classic development, for a budget comparable to a SaaS subscription.",
			},
			expoMigration: {
				name: "Expo migration",
				title: "React Native to Expo migration",
				description:
					"Assessment, plan, migration and handover: Lezo migrates your React Native app to Expo and cuts the cost of maintenance and new features by about four.",
			},
			useCases: {
				name: "Use cases",
				title: "Use cases: custom software by trade",
				description:
					"HVAC maintenance, QHSE consultancies, commercial cleaning, React Native app takeover: a typical day, what the trade requires and the tool Lezo would build for each situation.",
			},
			useCaseHvac: {
				name: "HVAC maintenance",
				title: "HVAC maintenance software for service contracts",
				description:
					"Custom HVAC maintenance software: visits planned from contracts, offline boiler-room readings, maintenance certificates generated on site and a portal for property managers, with no per-technician licence.",
			},
			useCaseQhse: {
				name: "QHSE consultancy",
				title: "Audit and action-plan software for QHSE consultancies",
				description:
					"A custom audit tool for QHSE consultancies: findings captured on site, risk rating with your method, reports in your branding and a client portal to follow action plans, sold as a service.",
			},
			useCaseCleaning: {
				name: "Commercial cleaning",
				title: "Quality-control software for cleaning companies",
				description:
					"Custom quality-control software for commercial cleaning: checklists per contract, scores computed on site, nonconformities tracked until fixed and a client portal that builds your renewal file.",
			},
			useCaseTakeover: {
				name: "React Native app takeover",
				title: "Take over a React Native app left by your vendor",
				description:
					"Your React Native app was delivered by an agency that left? Lezo recovers your accounts, assesses the app, brings it in line with Google Play and App Store requirements on Expo, without rewriting it.",
			},
			work: {
				name: "Work",
				title: "Work",
				description:
					"Projects built and operated by Lezo: the need, the solution, exactly what Lezo did and how the software is used.",
			},
			questoveryCase: {
				name: "Questovery",
				title: "Questovery, a web and mobile platform published by Lezo",
				description:
					"How Lezo designed, built and operates Questovery: web editor, mobile app and experience tracking.",
			},
			vaerdictCase: {
				name: "Vaerdict",
				title: "Vaerdict, a property investment simulator built by Lezo",
				description:
					"How Lezo built Vaerdict, the property investment simulation software for French wealth management advisers, and supported its rollout and commercial launch.",
			},
			chouicCase: {
				name: "Chouic",
				title: "Chouic: 3 React Native apps migrated to Expo in one month",
				description:
					"How Lezo migrated three mobile apps of the game studio Chouic from React Native to Expo in one month, and handed over the method that let the team migrate a fourth app on its own.",
			},
			products: {
				name: "Products",
				title: "Products",
				description:
					"Lezo publishes its own software, including Questovery, the platform to create and run treasure hunts and gamified tours on mobile.",
			},
			about: {
				name: "About",
				title: "About",
				description:
					"Lezo, a software development and publishing company: what we do, how we work together, our commitments and our founder.",
			},
			contact: {
				name: "Contact",
				title: "Contact",
				description:
					"Describe your application or how your team works today. Lezo will get back to you to clarify the need and the next step.",
			},
		},
	},
	nav: {
		brand: "Lezo",
		home: "Home",
		blog: "Blog",
		docs: "Documents",
		services: "Services",
		businessApps: "Business applications",
		businessAppsDescription:
			"Field data collection, reports, case tracking and client portal.",
		expoMigration: "Expo migration",
		expoMigrationDescription: "Migrate an existing React Native app to Expo.",
		useCases: "Use cases",
		useCasesAll: "All use cases",
		useCasesAllDescription: "Four trades, from a typical day to the tool.",
		work: "Work",
		products: "Products",
		about: "About",
		contact: "Let’s talk about your project",
		menu: "Menu",
		skip: "Skip to content",
		language: "Language",
	},
	common: {
		exampleBadge: "Example use case",
		shotPending: "Real screenshot to add",
		faqLabel: "FAQ",
		yourRole: "Your role",
		illustrationBadge: "Illustration",
	},
	home: {
		hero: {
			label: "AI-assisted development · Web and mobile · Software publishing",
			title: "Applications that help your business run better.",
			emphasis: "run better",
			intro:
				"Lezo designs your business software and modernises your web and mobile apps, with AI built into every step of development. The same quality for a fraction of yesterday’s cost, and the same point of contact from scoping to maintenance.",
			primaryCta: "Let’s talk about your project",
			secondaryCta: "Explore our services",
			entriesLabel: "Two ways in:",
			entries: {
				businessApps: "Custom business applications",
				expoMigration: "React Native to Expo migration",
			},
		},
		marquee: {
			label: "What we build and maintain",
			items: {
				a: "Case tracking",
				b: "Field data collection",
				c: "Reports",
				d: "Client portal",
				e: "Connections to your tools",
				f: "Expo migration",
				g: "iOS and Android",
				h: "Maintenance",
				i: "Improvements",
				j: "AI-assisted development",
			},
		},
		services: {
			label: "Services",
			title: "Two ways to work with Lezo",
			description:
				"Need better tools for your operations, or want to modernise an existing mobile app? Each offer has its own process and deliverables.",
			items: {
				businessApps: {
					eyebrow: "Business applications",
					title: "Less re-entry, better tracking of your operations.",
					description:
						"Case tracking, field data collection, reports, client portals: Lezo builds your business software, connects it to your tools and keeps improving it with you.",
					audience:
						"For managers and operations leads of specialised service companies.",
					points: {
						scoping: "Scoping with your users",
						firstVersion: "A first version focused on one use case",
						followUp: "Maintenance and new features after delivery",
						cost: "About five times cheaper than classic development",
					},
					link: "Explore business applications",
				},
				expoMigration: {
					eyebrow: "Expo migration",
					title: "Builds and releases that are easier to manage.",
					description:
						"Lezo analyses your React Native app, migrates it to Expo, checks the builds and hands over to your team.",
					audience: "For CTOs, engineering managers and mobile leads.",
					points: {
						diagnostic: "Assessment of the existing app",
						plan: "Migration plan and validation",
						handover: "Documentation and handover",
						cost: "Maintenance about four times cheaper",
					},
					link: "Explore Expo migration",
				},
			},
		},
		ai: {
			label: "AI-assisted development",
			title: "The same software, for about a fifth of the cost",
			description:
				"Business software used to cost around €100,000 to build, then €20,000 to €30,000 a year to maintain and improve. Lezo brings AI into every step of development, under the control of an experienced developer: the quality stays the same, the cost drops by about five.",
			saas: {
				title: "Your own software rather than a subscription?",
				description:
					"At this price, having a tool built around your process costs, over a few years, about the same as a SaaS subscription designed for everyone. With one difference: the tool is made for you, and you own it.",
				link: "Compare with a subscription",
			},
		},
		work: {
			label: "Work",
			title: "Questovery, designed, built and operated by Lezo",
			description:
				"Questovery lets professionals who welcome visitors create treasure hunts and gamified tours, offer them to their audience and follow how they are used.",
			facts: {
				role: {
					label: "Lezo’s role",
					value: "Design, development, production rollout and publishing",
				},
				platforms: {
					label: "Platforms",
					value: "Web editor, mobile app, experience tracking",
				},
				usage: {
					label: "Usage",
					value: "Year-round tours and one-off events",
				},
			},
			link: "Read the case study",
		},
		process: {
			label: "Working together",
			title: "A predictable collaboration, step by step",
			description:
				"At any point, you know what we’re working on, what you sign off and what comes next.",
			steps: {
				understand: {
					title: "Understand",
					description:
						"We look at the problem, the users, the existing tools and the constraints.",
					client: "You share how you work today and your priorities.",
				},
				scope: {
					title: "Scope",
					description:
						"We define a first engagement: its deliverables, timeline and price.",
					client: "You approve the scope and the proposal.",
				},
				build: {
					title: "Build",
					description: "We build in stages and show you progress regularly.",
					client: "You test and decide at each checkpoint.",
				},
				evolve: {
					title: "Hand over and evolve",
					description:
						"We deliver, document and organise operations, maintenance and new features.",
					client: "You choose the level of support and the next features.",
				},
			},
			note: "A first conversation is enough to see whether an assessment or scoping phase is useful. If so, you’ll know its scope and any cost before it starts.",
		},
		company: {
			label: "The company",
			title: "Building for our clients, publishing our own products",
			description:
				"Lezo is a software development and publishing company. We design web and mobile applications that fit how our clients work, modernise their existing apps and publish our own products, including Questovery.",
			pillars: {
				design: {
					title: "Design",
					description:
						"Web and mobile applications built around your real use cases, not a generic template.",
				},
				modernize: {
					title: "Modernise",
					description:
						"Existing applications taken over, stabilised and prepared for what comes next.",
				},
				publish: {
					title: "Publish",
					description:
						"Products we operate ourselves: releasing and maintaining software is part of our daily work.",
				},
			},
			link: "More about Lezo",
		},
		products: {
			label: "Our products",
			title: "Questovery",
			description:
				"Create and run your treasure hunts, gamified tours and mobile experiences. Features, plans and demos are on the product website.",
			link: "Discover Questovery",
			secondaryLink: "All products",
		},
		faq: {
			title: "Before you get in touch",
			items: {
				budget: {
					question: "How much does a project cost?",
					answer:
						"The budget depends on what already exists, the use cases and the chosen scope. As a rough guide, business software that would have cost around €100,000 is now built for about €20,000, thanks to AI-assisted development. After a first conversation, the proposal states what will be delivered, the schedule and the support terms. You can contact us without a set budget.",
				},
				brief: {
					question: "Do we need a full specification to start?",
					answer:
						"No. Describe how you work today or what is blocking you. Scoping is precisely where we define a limited first version and its success criteria.",
				},
				ai: {
					question: "Doesn’t AI lower the quality?",
					answer:
						"Not when it is supervised. AI writes part of the code, but an experienced developer sets the architecture, reviews every change and validates it with tests. You receive standard, documented code that another team can take over.",
				},
				after: {
					question: "What happens after delivery?",
					answer:
						"Depending on your choice, Lezo handles hosting, maintenance and improvements, or your team takes over using the documentation we deliver. Each part is priced separately.",
				},
				team: {
					question: "Do we need a technical team on our side?",
					answer:
						"Not for a business application: a contact person who knows the process and can involve the users is enough. For an Expo migration, a technical contact takes part in the assessment and the handover.",
				},
			},
		},
		cta: {
			title: "What problem do you want to solve?",
			description:
				"Describe your application or how your team works today. We’ll get back to you to talk it through.",
			button: "Describe my need",
		},
	},
	businessApps: {
		hero: {
			label: "Business applications",
			title: "Business tools that fit the way you work.",
			emphasis: "the way you work",
			intro:
				"Do you carry out site visits, produce reports or follow up with clients regularly? Lezo turns a process scattered across files, emails and tools into a single application, about five times cheaper to build than before thanks to AI.",
			primaryCta: "Describe my process",
			secondaryCta: "See the process",
		},
		situations: {
			label: "Situations",
			title: "Do any of these sound familiar?",
			description:
				"These needs are common in specialised service companies. During scoping, we check together whether they justify a custom tool.",
			items: {
				reentry: {
					title: "Information entered twice",
					description:
						"Readings, photos and notes are taken on site, then typed again at the office.",
				},
				reports: {
					title: "Reports that take too long to compile",
					description:
						"Every report means gathering files, copying tables and checking the same information again.",
				},
				tracking: {
					title: "Tracking that is hard to share",
					description:
						"Cases, interventions and action plans live in spreadsheets and inboxes; nobody has the full picture.",
				},
				clients: {
					title: "Clients asking where their case stands",
					description:
						"Your clients want to see their documents, follow progress or send information without going through email.",
				},
			},
		},
		economics: {
			label: "Cost",
			title: "Custom software is no longer reserved for big budgets",
			description:
				"Until recently, a custom business tool cost around €100,000, then €20,000 to €30,000 a year in maintenance and improvements. With supervised, AI-assisted development, Lezo delivers the same quality for about a fifth of that.",
		},
		saas: {
			label: "Subscription or software",
			title: "Your own software, for the price of a subscription",
			description:
				"A SaaS subscription is paid every month, per user, for as long as you use it. With development costs divided by five, a tool built around your process costs the same over five years, then less every year after.",
			subscription: {
				title: "A SaaS subscription",
				items: {
					fit: "Features designed for everyone, worked around for your case.",
					users: "A per-user price that grows with your team.",
					forever: "A cost that never stops.",
					roadmap: "Data and improvements that depend on the vendor.",
				},
			},
			custom: {
				title: "Your software",
				items: {
					fit: "Built around your actual process.",
					users: "A cost that does not depend on the number of users.",
					asset: "A tool you own, with its code and documentation.",
					roadmap: "Improvements decided by your priorities.",
				},
			},
			note: "Indicative assumption. Off-the-shelf software is sometimes the right answer: scoping compares both options.",
		},
		solutions: {
			label: "Example solutions",
			title: "What we can build",
			description:
				"These are only examples. Your solution starts from your actual process; sometimes adapting an existing tool is enough.",
			items: {
				collection: {
					title: "Mobile field data collection",
					description:
						"Forms, photos and readings captured on site and available at the office without re-entry.",
				},
				documents: {
					title: "Document generation",
					description:
						"Reports, minutes and summaries generated from collected data, in the format your clients expect.",
				},
				tracking: {
					title: "Case and action tracking",
					description:
						"Assignment, deadlines, statuses and history shared by the whole team.",
				},
				portal: {
					title: "Client portal",
					description:
						"A space where your clients see their documents, follow progress and send you information.",
				},
				integrations: {
					title: "Connections to your tools",
					description:
						"Data exchange with your existing software: accounting, CRM, file storage or messaging.",
				},
			},
		},
		usages: {
			label: "Two use cases",
			title: "Equip your team or extend your service",
			description:
				"A company may need one, the other, or both. We separate them from the start, because their value isn’t measured the same way.",
			benefitLabel: "Intended benefit",
			checkLabel: "Checked during scoping",
			items: {
				internal: {
					title: "Equip internal operations",
					benefit:
						"Reduce manual handling and help information flow between people.",
					check: "Process, frequency, time lost, users and integrations.",
				},
				clients: {
					title: "Extend a service to your clients",
					benefit:
						"Offer tracking, access to information or a new service to your clients.",
					check:
						"Real client demand, adoption, operations and any business model.",
				},
			},
		},
		process: {
			label: "Process",
			title: "From today’s process to a tool in daily use",
			description:
				"A limited first version, tested with your users, then new features decided with your business contact.",
			steps: {
				understand: {
					title: "Understand your process",
					description:
						"Interviews with the people involved, review of the files and tools in use, identification of friction points.",
				},
				compare: {
					title: "Compare the options",
					description:
						"Off-the-shelf tool, integration, adaptation or custom development: we recommend the best fit, even when it is not custom software.",
				},
				scope: {
					title: "Define a first version",
					description:
						"One priority use case, shared success criteria and mockups to validate how it works.",
				},
				build: {
					title: "Build and test",
					description:
						"Development in stages, regular demos and testing with the people who will use it.",
				},
				deploy: {
					title: "Roll out",
					description:
						"Go-live, migration of existing data when needed and support for the first users.",
				},
				evolve: {
					title: "Support and evolve",
					description:
						"Hosting, maintenance and improvements, as agreed in the contract.",
				},
			},
		},
		deliverables: {
			label: "Deliverables",
			title: "What each proposal specifies",
			items: {
				scope: "A scope and shared success criteria.",
				mockups: "User flows or mockups to validate how it works.",
				app: "An application or integration tested with your users.",
				docs: "The documentation needed to operate and take over the tool.",
				terms: "Access, delivery and support terms.",
			},
		},
		after: {
			label: "After delivery",
			title: "Business software needs care after launch",
			description:
				"Once in service, your tool is part of your team’s daily work. From the proposal onwards, we plan how it will be hosted, maintained and improved.",
			items: {
				hosting: {
					title: "Hosting and operations",
					description:
						"Deployment, backups and monitoring, in the environment chosen together.",
				},
				maintenance: {
					title: "Maintenance",
					description:
						"Fixes, security and dependency updates, monitoring that everything works.",
				},
				evolutions: {
					title: "New features",
					description:
						"Improvements prioritised with your business contact, based on user feedback.",
				},
				handover: {
					title: "Documentation and takeover",
					description:
						"Choices and operations are documented, so the tool can be taken over if your organisation changes.",
				},
			},
		},
		budget: {
			label: "Budget",
			title: "How the budget is built",
			description:
				"Budget and schedule depend on what exists, the use cases and the chosen scope. As a rough guide, allow about €20,000 for a first version and about €5,000 a year to keep it running. The proposal separates three parts:",
			items: {
				initial: {
					title: "Initial build",
					description: "Scoping, first version, testing and go-live.",
				},
				running: {
					title: "Running costs",
					description:
						"Hosting, maintenance and any third-party services, planned from the start.",
				},
				evolutions: {
					title: "New features",
					description:
						"Improvements decided as the tool is used, quoted separately.",
				},
			},
			note: "You can contact us without a set budget: the first conversation also helps size the investment.",
		},
		reassurance:
			"You bring your knowledge of the business. Lezo handles the technical choices and the build, with checkpoints defined together.",
		fit: {
			label: "Getting started",
			title: "What makes a good project",
			description:
				"You do not need a technical team. These conditions help launch a useful first version:",
			items: {
				process: "A real process you can show, and whose frequency you know.",
				referent: "A business contact who can make decisions.",
				users: "Users ready to test the workflows.",
				decision:
					"A decision-maker who backs the project and plans for the tool’s life after delivery.",
			},
		},
		proof: {
			label: "Proof",
			title: "A web and mobile product we operate",
			description:
				"Questovery combines a web editor, a mobile app and experience tracking. Lezo designed it, builds it and runs it every day: the same standards apply to our clients’ tools.",
			link: "Read the Questovery case study",
			note: "More references will be published once the clients involved agree.",
		},
		faq: {
			title: "Your questions about business applications",
			items: {
				offTheShelf: {
					question: "Why not use off-the-shelf software?",
					answer:
						"Sometimes that is the right answer. Scoping compares available solutions, integrating existing tools and custom development. With AI-assisted development, custom software costs about five times less than before: over a few years, it often matches the price of a subscription. We recommend it when your process justifies it.",
				},
				data: {
					question: "What happens to our existing files and data?",
					answer:
						"Their migration is studied during scoping: formats, volume, quality and history to keep. The proposal states what is migrated and how.",
				},
				adoption: {
					question: "How do we make sure the team will use the tool?",
					answer:
						"Users are involved from scoping and test the flows before go-live. The first version focuses on one use case, so it gets adopted before it grows.",
				},
				maintenance: {
					question: "Who maintains the application after delivery?",
					answer:
						"Lezo can take care of it: hosting, maintenance and improvements are set out in the contract. The documentation we deliver also lets another team take over the application.",
				},
				budget: {
					question: "How is the budget set?",
					answer:
						"From the scope of the first version, the integrations needed and the level of support you want. The proposal separates the build, running costs and new features.",
				},
			},
		},
		cta: {
			title: "How does your team work today?",
			description:
				"Where do you lose time? Who will use the tool? A few lines are enough to prepare the conversation.",
			button: "Describe my process",
		},
	},
	expoMigration: {
		hero: {
			label: "React Native to Expo migration",
			title: "Move your React Native application to Expo.",
			emphasis: "to Expo",
			intro:
				"Are builds, upgrades or releases taking up too much of your team’s time? Lezo moves your app to Expo and cuts the cost of its maintenance and upcoming features by about four.",
			primaryCta: "Talk about my app",
			secondaryCta: "See the process",
		},
		situations: {
			label: "Situations",
			title: "When to consider a migration",
			items: {
				builds: {
					title: "Fragile builds",
					description:
						"iOS or Android builds fail intermittently or only work on one particular machine.",
				},
				release: {
					title: "Complex releases",
					description:
						"Signing, versioning and publishing to the stores involve poorly documented manual steps.",
				},
				native: {
					title: "Heavy native maintenance",
					description:
						"The ios/ and android/ folders pile up changes nobody dares touch, and every React Native upgrade turns into a project.",
				},
				departure: {
					title: "A key developer leaving",
					description:
						"Project knowledge rests on one person who is leaving, or on a provider you are replacing.",
				},
				adoption: {
					title: "A decision to adopt Expo",
					description:
						"Your team wants Expo tooling: EAS builds and submissions, updates, native configuration through plugins.",
				},
				roadmap: {
					title: "A major change ahead",
					description:
						"A redesign or a major feature is planned, and you want a sound foundation first.",
				},
			},
		},
		economics: {
			label: "Cost",
			title: "An Expo app costs about four times less to evolve",
			description:
				"On an older React Native app, maintenance often runs around €30,000 a year, and a new feature about €10,000, between broken builds and native code to adapt. Once the app is on Expo, these costs drop by about four.",
			reasonsTitle: "Where the savings come from",
			reasons: {
				native: {
					title: "Less native code to maintain",
					description:
						"The ios/ and android/ folders are generated from configuration: no more manual changes to carry over at every update.",
				},
				upgrades: {
					title: "Planned upgrades",
					description:
						"The Expo SDK bundles versions that work together: an upgrade becomes routine work again.",
				},
				builds: {
					title: "Automated builds and releases",
					description:
						"EAS builds, signs and submits the app to the stores, without relying on one particular machine.",
				},
				ai: {
					title: "A migration sped up by AI",
					description:
						"AI handles the repetitive changes; every step is validated by the builds and the key user flows.",
				},
			},
		},
		process: {
			label: "Process",
			title: "Four steps, with results presented at each one",
			workLabel: "Work done",
			resultLabel: "What you get",
			steps: {
				diagnostic: {
					title: "Assessment",
					work: "Review the existing app, dependencies, constraints and goals.",
					result: "A clear decision: whether to migrate, and on what scope.",
				},
				plan: {
					title: "Migration plan",
					work: "Define the changes, their order and how they will be validated.",
					result:
						"A plan discussed with your team and an estimate specific to the project.",
				},
				build: {
					title: "Implementation and validation",
					work: "Adapt the project, then verify the builds and key user flows.",
					result: "A delivered version with the verification results.",
				},
				handover: {
					title: "Handover",
					work: "Document the choices and hand over to your team.",
					result: "A team able to build and release on its own.",
				},
			},
		},
		decisions: {
			label: "After the assessment",
			title: "What we decide together",
			description:
				"There is no single migration recipe. The assessment lets us settle, with your team:",
			items: {
				tools: {
					title: "The tooling",
					description:
						"Which Expo tools to adopt (prebuild, EAS Build, EAS Submit, updates) and which existing tools to keep.",
				},
				pace: {
					title: "The pace of adoption",
					description:
						"A gradual adoption, one part at a time, or a broader switch in one go.",
				},
				native: {
					title: "Native integrations",
					description:
						"For each native dependency: an Expo module, an existing config plugin, a plugin to write or native code to keep.",
				},
				delivery: {
					title: "The release pipeline",
					description:
						"Signing, environments, build profiles and who is responsible for store releases.",
				},
			},
		},
		limits: {
			title: "What a migration does not fix on its own",
			description:
				"Benefits depend on your project. A migration simplifies native configuration and the build pipeline; it does not automatically fix functional debt, performance or the app’s architecture. The assessment clearly separates what the migration covers from what it does not.",
		},
		deliverables: {
			label: "Deliverables",
			title: "What you receive",
			items: {
				diagnostic:
					"A written assessment of the existing app and its dependencies.",
				plan: "An engagement plan with the order of changes.",
				code: "The code and configuration changes.",
				validation: "Validation results: builds and tested user flows.",
				docs: "Documentation of the choices and the new setup.",
				session: "A handover session with your team.",
			},
			note: "Required access, release responsibilities, timelines, support and acceptance criteria are set out in the proposal.",
		},
		proof: {
			label: "Case study",
			title: "Chouic: three apps migrated to Expo in one month",
			description:
				"Chouic, a studio whose mobile games are played by millions, asked Lezo to migrate three React Native apps. Its team then migrated a fourth app on its own, and reports a productivity gain and lower maintenance costs.",
			link: "Read the Chouic case study",
			note: "Lezo also builds its own mobile app, Questovery, with Expo, and has released and maintained it since day one.",
		},
		faq: {
			title: "Your questions about Expo migration",
			items: {
				rewrite: {
					question: "Does migrating mean rewriting the application?",
					answer:
						"No, that is not the goal. Most of the application code is usually kept; it is mainly the native configuration, some dependencies and the build pipeline that change. The assessment identifies any parts that need deeper changes.",
				},
				native: {
					question: "How are native dependencies and integrations handled?",
					answer:
						"Each dependency is reviewed: Expo equivalent, existing config plugin, plugin to write or native code to keep. The choices are documented in the plan and approved with your team.",
				},
				access: {
					question: "What access do you need to review the project?",
					answer:
						"For the assessment: read access to the code repository and a description of the environments. Access to store accounts and third-party services is only requested if the migration requires it, under rules agreed with you.",
				},
				handover: {
					question: "How do you prepare our team to take over?",
					answer:
						"Your developers take part in validation, the documentation describes the new setup and a handover session is planned. The goal is for your team to build and release without depending on Lezo.",
				},
				pricing: {
					question: "How are price and schedule set?",
					answer:
						"After the assessment, based on the native dependencies to handle, the expected validation and your team’s availability. The migration plan comes with an estimate specific to the project. Once migrated, maintenance and new features usually cost about four times less.",
				},
			},
		},
		cta: {
			title: "Where does your app stand?",
			description:
				"Which app do you want to migrate? What is your main blocker? These two answers are enough to prepare a first conversation.",
			button: "Talk about my app",
		},
	},
	useCases: useCasesEn,
	work: {
		hero: {
			label: "Work",
			title: "What Lezo has built and operates",
			emphasis: "built and operates",
			intro:
				"For each project: the need, the solution, exactly what Lezo did and how the software is used.",
		},
		roleLabel: "Lezo’s role",
		scopeLabel: "Scope",
		items: {
			vaerdict: {
				badge: "Client · Business software",
				title: "Vaerdict",
				summary:
					"A property investment simulator for wealth management advisers: studies for every scheme, yearly follow-up of each investment and a client base that flags the meetings to propose.",
				role: "Development of the application, support for its rollout and commercial launch.",
				scope: "Calculation engine · Web application · Client base and alerts",
				link: "Read the case study",
				visualAlt: "Preview of an adjusted Vaerdict simulation",
			},
			chouic: {
				badge: "Client · Expo migration",
				title: "Chouic",
				summary:
					"Three mobile game apps, played by millions, migrated from React Native to Expo in one month. The Chouic team then migrated a fourth app on its own.",
				role: "Assessment, migration and release of the three apps, then handover of the method to the team.",
				scope: "3 iOS and Android apps · EAS builds · Reusable method",
				link: "Read the case study",
				visualAlt: "Three mobile games moved from React Native to Expo",
			},
			questovery: {
				badge: "Product published by Lezo",
				title: "Questovery",
				summary:
					"A web and mobile platform to create, offer and track treasure hunts and gamified tours.",
				role: "Design, development, production rollout and operations.",
				scope: "Web editor · Mobile app · Experience tracking",
				link: "Read the case study",
				visualAlt: "Preview of the Questovery editor",
			},
		},
		policy: {
			title: "How we present our work",
			description:
				"We only show projects where Lezo’s role can be verified and the results are documented. Client references are published with the agreement of the companies involved; some projects deliberately remain anonymous.",
		},
		upcoming: {
			title: "More case studies to come",
			description:
				"New case studies will be added as projects progress. Want to know whether we have handled a need similar to yours? Just ask.",
		},
		cta: {
			title: "A project in mind?",
			description:
				"Tell us about your situation; we will tell you honestly whether Lezo is the right partner.",
			button: "Let’s talk about your project",
		},
	},
	questoveryCase: {
		hero: {
			label: "Case study · Product published by Lezo",
			title:
				"Questovery: a web and mobile platform designed and operated by Lezo",
			emphasis: "designed and operated",
			intro:
				"How Lezo built a complete product, from web editor to mobile app and tracking, and keeps it evolving with its users.",
		},
		facts: {
			role: {
				label: "Lezo’s role",
				value: "Design, development, production rollout, publishing",
			},
			platforms: {
				label: "Platforms",
				value: "Web and mobile (iOS, Android)",
			},
			status: {
				label: "Status",
				value: "In production",
			},
		},
		situation: {
			title: "Situation",
			body: "Professionals who welcome visitors (heritage sites, agencies, event organisers) want to offer gamified mobile experiences without building an app each time. They need to design their scenarios, offer them to their audience and follow how they are used.",
		},
		intervention: {
			title: "What Lezo did",
			body: "Lezo designed and built the whole product and publishes it: technical choices, development, store releases, hosting, maintenance and new features.",
		},
		solution: {
			title: "Solution",
			body: "Questovery combines three building blocks:",
			items: {
				editor: {
					title: "A web editor",
					description:
						"To design the scenarios, steps and content of each experience.",
				},
				mobile: {
					title: "A mobile app",
					description: "To live the experience on site, step by step.",
				},
				tracking: {
					title: "Experience tracking",
					description: "To manage offers and see how they are used.",
				},
			},
			shots: {
				editor: "Questovery web editor",
				mobile: "Questovery mobile app",
				tracking: "Experience tracking",
			},
		},
		usage: {
			title: "Usage",
			body: "Château Giscours uses Questovery for tours offered all year round and for events such as its open days. Agencies and works councils also use it; at their request, they are not named here.",
			note: "This reference illustrates the use of the Questovery product. It is not a custom engagement carried out by Lezo for Château Giscours.",
		},
		next: {
			title: "What’s next",
			body: "Lezo keeps improving Questovery based on user feedback, including apps branded for its clients. Running our own product shapes how we design and maintain our clients’ applications.",
		},
		productLink: "Discover Questovery",
		cta: {
			title: "A web and mobile project to build?",
			description:
				"Tell us about your need: business application, client portal or an existing mobile app to modernise.",
			button: "Let’s talk about your project",
		},
	},
	vaerdictCase: {
		hero: {
			label: "Case study · Client",
			title: "Vaerdict: the property simulator for wealth management advisers",
			emphasis: "property simulator",
			intro:
				"Lezo built the entire application, turned the rules of wealth management into calculations and supported the Vaerdict team through rollout and commercial launch.",
		},
		facts: {
			role: {
				label: "Lezo’s role",
				value: "Development, rollout, commercial launch support",
			},
			users: {
				label: "Users",
				value:
					"Wealth management advisers, banking networks, property developers",
			},
			status: {
				label: "Status",
				value: "Sold as a subscription",
			},
		},
		productLink: "Discover Vaerdict",
		situation: {
			title: "Situation",
			body: "Wealth management advisers (CGP in France) support clients who invest in property: new builds, older buildings or real estate funds (SCPI), under tax schemes with very different rules (furnished rentals, property deficit, heritage buildings…). To advise, then reassure over time, they need to show what an investment really earns, rent, tax, loan, charges and resale included. Vaerdict’s founders come from this profession and wanted a tool that runs this calculation rigorously and follows each investment year after year.",
		},
		intervention: {
			title: "Lezo’s work",
			body: "Lezo built the application end to end. The work started with the profession: how an adviser builds a study, which rules apply to each scheme, how financing and resale weigh on the result. That logic became a calculation engine, then screens advisers use in front of their clients. Lezo then supported the founders and the Vaerdict team in rolling out the solution and selling it.",
		},
		solution: {
			title: "Solution",
			body: "Vaerdict brings together in a single web application:",
			items: {
				simulation: {
					title: "Simulations for every scheme",
					description:
						"New builds, older properties, SCPI: a full study of the investment, from financing to resale.",
				},
				followUp: {
					title: "Follow-up over time",
					description:
						"Every year, the adviser enters the actual figures and compares the result with the reference study.",
				},
				opportunities: {
					title: "A client base that creates meetings",
					description:
						"Alerts flag the clients to see again and turn follow-up into advice opportunities.",
				},
				whiteLabel: {
					title: "White label and PDF studies",
					description:
						"Screens and documents in the firm’s colours, scenario comparison, studies ready to hand over.",
				},
			},
		},
		engine: {
			title: "Turning the profession into calculations",
			body: "At the heart of Vaerdict is a calculation engine. Every study follows the same chain, built by Lezo from the founders’ method:",
			steps: {
				revenue: {
					symbol: "A",
					title: "Total income",
					detail: "Rent, tax reduction, recovered VAT.",
				},
				expenses: {
					symbol: "B",
					title: "Total expenses",
					detail: "Charges, loan repayments, tax.",
				},
				balance: {
					symbol: "C = A − B",
					title: "Net cash flow",
					detail:
						"The savings put in, or the income generated, over the whole period.",
				},
				capital: {
					symbol: "D",
					title: "Net capital built",
					detail: "The resale price, minus the capital still owed to the bank.",
				},
				performance: {
					symbol: "C + D",
					title: "Gain, IRR, net yield",
					detail:
						"What the investment really earned, comparable from one study to the next.",
				},
			},
			note: "The tax rules for property restoration schemes (property deficit, historic monuments, Malraux, Denormandie) were validated by the law firm Rivière Avocats Associés.",
		},
		followUp: {
			label: "Yearly follow-up",
			title: "Reassuring with figures, not with speeches",
			body: "A study does not end at signature. Every year, the adviser updates the actual figures (rent, charges, rates, property valuation) and shows the client where the investment stands. When a lower valuation worries a client, the answer is a calculation. Alerts in the client base show who to see again, and when.",
		},
		shots: {
			simulation:
				"Adjusted simulation: reference study and recalculated result",
			followUp: "Client base: alerts and meetings to propose",
		},
		rollout: {
			title: "Rollout and commercial launch",
			body: "Lezo supported the founders and the Vaerdict team beyond development: production rollout, deployment to advisers and support for selling the offer.",
		},
		today: {
			title: "Vaerdict today",
			body: "Vaerdict is sold as a subscription to wealth management advisers, banking networks and property developers, with a white-label option. The application passed a penetration test, and Vaerdict is backed by an honour loan from Bpifrance.",
			note: "Vaerdict is presented with its founders’ agreement. The clients, properties and amounts shown in the illustrations are fictitious.",
		},
		cta: {
			title: "Software to build around your expertise?",
			description:
				"You have a method, rules and calculations you master: let’s talk about the tool that will put them in the hands of your teams and your clients.",
			button: "Let’s talk about your project",
		},
	},
	chouicCase: {
		hero: {
			label: "Case study · React Native to Expo migration",
			title: "Chouic: three mobile apps moved to Expo in one month",
			emphasis: "in one month",
			intro:
				"Chouic publishes mobile games for couples and groups of friends, played by millions. Lezo migrated three of its React Native apps to Expo in one month; the Chouic team then migrated a fourth app on its own, with the method and tooling handed over.",
		},
		siteLink: "Visit Chouic",
		facts: {
			client: {
				label: "Client",
				value: "Chouic, mobile game studio in Nantes, France",
			},
			scope: {
				label: "Engagement",
				value: "3 iOS and Android apps migrated in 1 month",
			},
			after: {
				label: "Afterwards",
				value: "A 4th app migrated by the Chouic team",
			},
		},
		client: {
			label: "The client",
			title: "A studio making games for couples and friends",
			body: "Chouic is a studio from Nantes that makes mobile apps and board games. Its apps (couple quizzes, truth or dare, party games) bring together millions of players: Action ou Vérité (Truth or Dare) alone has passed 30 million downloads.",
			team: "Design, development, question writing and marketing are all done in-house, by a team running several apps side by side.",
			logoAlt: "Chouic",
			tagline: "Mobile game and board game studio · Nantes",
			portfolioLabel: "Some Chouic apps",
			portfolio: {
				lovbirdz: "LovBirdz",
				truthOrDare: "Action ou Vérité",
				spiky: "Spiky",
				koopla: "Koopla",
			},
		},
		challenge: {
			label: "Challenge",
			title: "Ageing apps that kept the team busy",
			description:
				"Over the years, Chouic built up a portfolio of React Native apps that still had to be maintained. Each app had its own native setup, so every maintenance task was multiplied by the number of apps.",
			items: {
				builds: {
					title: "Fragile builds",
					description:
						"One tooling or dependency update was enough to break an iOS or Android build, and fixing it came before anything else.",
				},
				upgrades: {
					title: "Postponed upgrades",
					description:
						"Upgrading React Native meant reworking the native code of every app: a project that kept being pushed back.",
				},
				stores: {
					title: "Tedious releases",
					description:
						"Signing, versioning and submitting each app to two stores took manual steps, repeated across the whole portfolio.",
				},
				roadmap: {
					title: "Less time for the games",
					description:
						"Time spent maintaining what existed was missing for evolving the games: new modes, new questions, new features.",
				},
			},
		},
		intervention: {
			label: "What Lezo did",
			title: "Three apps migrated in one month, one at a time",
			description:
				"Lezo migrated the apps one after the other, each released before moving on to the next. Players simply received an update of the app they already had installed.",
			steps: {
				diagnostic: {
					title: "One assessment per app",
					description:
						"Native dependencies, configuration, signing and release: for each app, what moves to Expo as is and what needs adapting.",
				},
				migration: {
					title: "A migration validated app by app",
					description:
						"Native configuration turned into plugins, dependencies replaced or adapted, builds and submissions through EAS. Each app is checked on its key flows before the next one.",
				},
				handover: {
					title: "A method handed over",
					description:
						"The choices, steps and migration tooling are documented and reusable: the Chouic team applied them on its own to a fourth app.",
				},
			},
		},
		results: {
			label: "Results",
			title: "Lighter maintenance and time back for new features",
			description:
				"Chouic reports a productivity gain and lower maintenance costs for its apps. The time freed up goes back into evolving the games.",
			stats: {
				apps: { value: "3", label: "apps migrated by Lezo" },
				month: { value: "1 month", label: "for all three migrations" },
				autonomy: {
					value: "+1",
					label: "app then migrated by the Chouic team on its own",
				},
			},
			points: {
				productivity: {
					title: "A productivity gain",
					description:
						"Builds and submissions run through EAS: fewer manual steps, no more reliance on one particular machine.",
				},
				maintenance: {
					title: "Lower maintenance costs",
					description:
						"Native folders are generated from configuration, and upgrades follow the Expo SDK.",
				},
				features: {
					title: "New features again",
					description:
						"The team spends its time evolving its games again instead of repairing their builds.",
				},
			},
		},
		next: {
			title: "Why this engagement worked",
			body: "Migrating several apps from the same publisher lets the work compound: the first migration sets the method, the next ones reuse it. That is how three migrations fit in one month, and how the Chouic team then led the fourth without Lezo.",
		},
		scenes: {
			legacy: "React Native",
			expo: "Expo",
			phones: {
				couple: {
					game: "Couple quiz",
					prompt: "Which of you is the better cook?",
					a: "Me",
					b: "You",
				},
				dare: {
					game: "Truth or dare",
					prompt: "Impersonate the person on your left for 30 seconds.",
					a: "Dare",
					b: "Truth",
				},
				friends: {
					game: "Friends quiz",
					prompt: "Who would make the best secret agent?",
					a: "Sam",
					b: "Ines",
				},
			},
			quiz: {
				game: "Couple quiz",
				round: "Question 4 / 10",
				question: "Which of you forgets birthdays the most?",
				players: { a: "Lea", b: "Hugo" },
				waiting: "Hugo is thinking…",
				match: "Same answer!",
				points: "+10 pts",
				score: "Chemistry",
			},
			timeline: {
				title: "How the engagement ran",
				weeks: { a: "W1", b: "W2", c: "W3", d: "W4" },
				after: "Later",
				apps: {
					a: "App 1",
					b: "App 2",
					c: "App 3",
					d: "App 4",
				},
				phases: {
					diagnostic: "Assessment",
					migration: "Migration",
					release: "Release",
				},
				byLezo: "Migrated by Lezo",
				byClient: "Migrated by the Chouic team",
				done: "On Expo",
				note: "Illustration of the schedule: the length of each step varied from app to app.",
			},
			feed: {
				title: "Released updates",
				build: "EAS build",
				published: "Released",
				stores: "iOS · Android",
				items: {
					mode: "New game mode",
					pack: "Pack of 200 questions",
					sdk: "Expo SDK upgrade",
					friends: "Challenges between friends",
				},
			},
		},
		cta: {
			title: "Are your React Native apps taking up too much of your time?",
			description:
				"Tell us about your apps: we will tell you which ones to migrate first, how long it takes, and what your team can then do on its own.",
			button: "Review my apps with Lezo",
		},
	},
	products: {
		hero: {
			label: "Products",
			title: "Lezo also publishes its own software",
			emphasis: "its own software",
			intro:
				"Running them every day directly shapes how we design and maintain our clients’ applications.",
		},
		questovery: {
			badge: "Available",
			title: "Questovery",
			tagline:
				"Create and run your treasure hunts, gamified tours and mobile experiences.",
			description:
				"A platform for professionals who want to design their scenarios, sell their offers and manage their experiences, with the option of an app under their own brand.",
			features: {
				design: "Design scenarios",
				sell: "Sell offers",
				run: "Manage experiences",
				brand: "An app under your brand",
			},
			link: "Discover Questovery",
			caseLink: "Read the case study",
			visualAlt: "Preview of the Questovery mobile app",
		},
		why: {
			label: "Why we publish",
			title: "What publishing brings to our clients",
			items: {
				operations: {
					title: "Day-to-day operations",
					description:
						"We handle releases, monitoring and support for software people actually use.",
				},
				longevity: {
					title: "The long run",
					description:
						"A product is maintained for years: dependencies, upgrades, new features. We plan for this in your projects.",
				},
				stores: {
					title: "Mobile releases",
					description:
						"Builds, signing and publishing to the App Store and Google Play are routine for us.",
				},
			},
		},
		whiteLabel: {
			title: "A branded treasure hunt app?",
			description:
				"For adaptations of the Questovery engine or white-label apps, talk directly to the Questovery team.",
			link: "See Questovery plans",
		},
		upcoming: {
			title: "More products on the way",
			description: "We’ll show them here once they’re ready.",
		},
	},
	about: {
		hero: {
			label: "About",
			title: "Lezo, software development and publishing.",
			emphasis: "publishing",
			intro:
				"Lezo designs custom web and mobile applications, modernises existing apps and builds its own products, including Questovery.",
		},
		activity: {
			label: "What we do",
			title: "Three activities, one standard",
			items: {
				services: {
					title: "Development services",
					description:
						"Custom business applications, client portals and React Native to Expo migrations.",
				},
				products: {
					title: "Software publishing",
					description:
						"Products designed, sold and operated by Lezo, starting with Questovery.",
				},
				support: {
					title: "Maintenance and improvements",
					description:
						"Hosting, fixes and new features for the applications we deliver.",
				},
			},
		},
		method: {
			label: "Method",
			title: "How we work with you",
		},
		principles: {
			label: "Commitments",
			title: "Our working principles",
			items: {
				direct: {
					title: "A direct contact",
					description:
						"You talk with the person who understands your need and builds the project.",
				},
				scope: {
					title: "An explicit scope",
					description:
						"Each engagement states what is delivered, what is not and what will be decided later.",
				},
				ai: {
					title: "Supervised AI",
					description:
						"AI lets us deliver faster and for less, never without review, tests and a developer’s decision.",
				},
				checkpoints: {
					title: "Checkpoints",
					description:
						"You see progress regularly and decide at the key steps.",
				},
				docs: {
					title: "Useful documentation",
					description:
						"Choices and operations are documented for running and taking over the software.",
				},
				after: {
					title: "Clear responsibilities after delivery",
					description:
						"Hosting, maintenance and new features: who does what is defined in the proposal.",
				},
			},
		},
		founder: {
			label: "The founder",
			title: "Simon Boisset",
			description:
				"Lezo was founded by Simon Boisset, software developer and creator of Questovery. Simon runs the company and works on the projects directly, from understanding the need to delivery.",
			portraitAlt: "Portrait of Simon Boisset",
			link: "View Simon Boisset’s profile",
		},
		practical: {
			label: "Practical information",
			title: "Working with Lezo",
			items: {
				location: {
					label: "Location",
					value: "France",
				},
				collaboration: {
					label: "Collaboration",
					value: "Remote, with on-site meetings when the project calls for it",
				},
			},
			emailLabel: "Email",
		},
		staffing: {
			title: "Looking for a developer to join your team?",
			description:
				"Lezo takes on projects with defined deliverables. For a developer joining your team, see Simon Boisset’s profile.",
			link: "View profile",
		},
		cta: {
			title: "Let’s talk about your project",
			description:
				"Describe your situation and what you want to improve. We will get back to you to define the next step.",
			button: "Describe my need",
		},
	},
	contact: {
		hero: {
			label: "Contact",
			title: "Let’s talk about your application or your business need.",
			emphasis: "your business need",
			intro:
				"Describe your current situation and what you want to improve. We will get back to you to clarify the need and the possible next steps.",
		},
		form: {
			title: "Your request",
			requiredHint: "Fields not marked “optional” are required.",
			optional: "optional",
			name: "Name",
			email: "Work email",
			company: "Company",
			need: "Need",
			needPlaceholder: "Choose a need",
			needOptions: {
				businessApps: "Business application",
				expoMigration: "React Native to Expo migration",
				other: "Other need",
			},
			situation: "Situation and goal",
			situationHints: {
				businessApps:
					"How do you work today? Where do you lose time? Who will use the tool?",
				expoMigration:
					"Which app do you want to migrate? What is your main blocker?",
				other: "Describe your current situation and the result you want.",
			},
			timeline: "Expected timeline",
			timelinePlaceholder: "e.g. go-live before summer",
			budget: "Expected budget",
			budgetPlaceholder: "A range is enough, or “to be defined”",
			website: "Website or public link to the application",
			websitePlaceholder: "https://",
			honeypot: "Leave this field empty",
			privacy:
				"Your information is only used to reply to your request and is kept for no more than three years after your last contact, for a prospect without a contract. No need to share internal data or code access here; we’ll ask later if needed.",
			privacyLink: "Privacy policy",
			submit: "Send my request",
			submitting: "Sending…",
			errors: {
				summary: "Some fields need your attention.",
				required: "This field is required.",
				email: "Enter a valid email address.",
				situation: "Add a few more details (at least 20 characters).",
				url: "Enter an address starting with http:// or https://.",
			},
			success: {
				title: "Your request has been received.",
				description: "We will get back to you at the address you provided.",
				booking: {
					description: "To save time, pick a slot now to talk it through.",
					button: "Book a slot",
					fallback:
						"Otherwise, we will get back to you at the address you provided.",
				},
				again: "Send another request",
			},
			failure: {
				title: "Your request could not be sent.",
				description:
					"Your answers are still in the form. Try again or email us directly:",
			},
		},
		aside: {
			next: {
				title: "After your message",
				steps: {
					read: {
						title: "We read your request",
						description: "To check that it matches one of Lezo’s offers.",
					},
					call: {
						title: "We talk it through",
						description:
							"In a call you can book as soon as you send your request, to clarify the need, the people involved and the schedule.",
					},
					proposal: {
						title: "We agree on the next step together",
						description:
							"Assessment, scoping or proposal: you’ll know what it covers and any cost before we start.",
					},
				},
			},
			direct: {
				title: "Prefer to talk directly?",
				booking: "Book a call in our calendar:",
				bookingLink: "Choose a slot",
				email: "Or email us:",
			},
			orientation: {
				title: "A different request?",
				staffing: {
					text: "Looking for a developer to join your team?",
					link: "Simon Boisset’s profile",
				},
				product: {
					text: "A treasure hunt or a gamified tour on mobile?",
					link: "Discover Questovery",
				},
			},
		},
	},
	economics: {
		note: "Indicative orders of magnitude: the actual estimate depends on your scope.",
		businessApp: {
			title: "Cost of business software",
			before: "Classic development",
			after: "With Lezo",
			rows: {
				build: "Build",
				yearly: "Maintenance and improvements, per year",
			},
		},
		expo: {
			title: "Cost of a mobile app",
			before: "Older React Native",
			after: "After the Expo migration",
			rows: {
				yearly: "Maintenance, per year",
				feature: "One new feature",
			},
		},
		methodTitle: "How we use AI",
		method: {
			decide: {
				title: "AI writes, a developer decides",
				description:
					"Architecture, technical choices and the approval of every change stay in the hands of an experienced developer.",
			},
			verify: {
				title: "Every change is checked",
				description:
					"Code review, automated tests and regular demos: AI speeds up delivery, with no shortcuts on quality.",
			},
			own: {
				title: "Standard code that you own",
				description:
					"Widely used technologies, readable and documented code: another team can take it over at any time.",
			},
		},
		saas: {
			title: "Cumulative cost over six years",
			subscription: {
				name: "SaaS subscription",
				detail: "{users} users at {price} per month",
			},
			custom: {
				name: "Your software with Lezo",
				detail: "{build} to build, then {yearly} per year",
			},
			crossing: "Same budget at {years} years",
			summary:
				"After {years} years, a SaaS subscription and your software have each cost about {total}. After that, the subscription costs {saas} per year, your software {custom}.",
		},
	},
	scenes: {
		host: "app.your-company.com",
		aiFlow: {
			file: "reports/export.ts",
			stages: {
				generate: "AI",
				review: "Review",
				tests: "Tests",
				live: "Live",
			},
			request: "PDF export of reports",
			aiChip: "Drafted by AI",
			reviewChip: "In review",
			reviewer: "DEV",
			comment: "Handle reports without photos.",
			code: {
				a: "export async function exportReport(id) {",
				b: "  const report = await reports.get(id);",
				c: "  const pdf = await renderPdf(report);",
				d: "  return files.save(pdf);",
				e: "}",
			},
			fix: "  report.photos ??= [];",
			checksTitle: "Checks",
			checks: {
				unit: { label: "Automated tests", value: "48/48" },
				journey: { label: "Field → report flow", value: "OK" },
				builds: { label: "Web, iOS and Android builds", value: "OK" },
			},
			live: {
				title: "Release",
				version: "Version 1.4 in production",
				detail: "PDF export is available to the whole team.",
				signoff: "Reviewed, tested and approved by a developer",
			},
		},
		assemble: {
			appName: "Service tracking",
			status: "Everything in one place",
			sources: {
				email: "Email",
				sheet: "Spreadsheet",
				photo: "Photo",
				note: "Sticky note",
				pdf: "PDF",
			},
			modules: {
				request: "Client request",
				planning: "Schedule",
				survey: "Field survey",
				actions: "Actions",
				report: "Report",
			},
			request: {
				subject: "Annual visit, Linden Court residence",
				from: "Claire Martin",
				time: "9:12 AM",
			},
			planning: {
				a: { who: "Team A", when: "Mon 14" },
				b: { who: "Team B", when: "Tue 15" },
				c: { who: "Team A", when: "Thu 17" },
			},
			survey: {
				label: "Boiler room, building B",
				meta: "4 photos · 12 readings",
			},
			actions: {
				a: "Replace the valve seal",
				b: "Schedule the inspection",
				c: "Send the report",
			},
			report: { title: "Visit report", status: "Generated" },
		},
		field: {
			formTitle: "Survey · Boiler room B",
			pressure: "Pressure",
			pressureValue: "1.8 bar",
			state: "Overall condition",
			stateValue: "Compliant",
			photos: "Photos",
			send: "Send survey",
			sent: "Survey sent",
			dashTitle: "Interventions",
			sync: "Synced",
			rows: {
				a: { site: "Linden Court residence", status: "Report ready" },
				b: { site: "Jaurès school group", status: "Scheduled" },
				c: { site: "Town hall annex", status: "In progress" },
			},
		},
		pipeline: {
			file: "app.config.ts",
			stages: {
				diagnostic: "Assessment",
				plan: "Plan",
				build: "Builds",
				handover: "Handover",
			},
			deps: {
				title: "Native dependencies",
				a: { name: "react-native-camera", verdict: "Expo module" },
				b: { name: "react-native-firebase", verdict: "Config plugin" },
				c: { name: "internal-payment-sdk", verdict: "Plugin to write" },
				d: { name: "bluetooth-module", verdict: "Native code kept" },
			},
			plan: {
				title: "Migration plan",
				a: "Native configuration as plugins",
				b: "Dependencies, one by one",
				c: "Build profiles and signing",
				d: "Store publishing",
			},
			build: {
				title: "Builds and verification",
				ios: "iOS build",
				android: "Android build",
				submit: "Store submission",
				flows: "Key user flows tested",
			},
			handover: {
				title: "Handover",
				doc: "New workflow documented",
				session: "Session with your team",
				owner: "Your team builds and ships",
			},
		},
		quest: {
			title: "The estate’s secret",
			step: "Step 3 / 8",
			place: "The orangery",
			approach: "You are getting close",
			distance: "40 m",
			question: "How many columns hold up the facade?",
			answers: { a: "4", b: "6", c: "8" },
			validated: "Step completed",
			points: "+120 pts",
			ranking: "Leaderboard",
			teams: { a: "The Foxes", b: "Team Orange", c: "The Curious" },
		},
		editor: {
			steps: {
				a: "Welcome",
				b: "The main courtyard",
				c: "The orangery",
				d: "The wine cellar",
			},
			stepType: "Riddle",
			trigger: "GPS trigger",
			radius: "30 m radius",
			publish: "Publish",
		},
		tracking: {
			title: "Games in progress",
			live: "Live",
			done: "Finished",
			running: "Playing",
		},
		blueprint: {
			notes: {
				a: "“We re-type everything at night”",
				b: "“Clients chase us by email”",
				c: "3 tools, 2 spreadsheets",
			},
			flow: { a: "Visit", b: "Survey", c: "Report", d: "Send" },
			scope: "V1 · one priority use",
			demo: "Demo approved",
			live: "v1.0 in service",
			docs: "Documentation",
			next: "Improvements",
		},
		vaerdict: {
			nav: {
				contacts: "Contacts",
				files: "Files",
				simulation: "Simulation",
				alerts: "Alerts",
			},
			client: "Mr and Mrs Durand",
			project: "Flat · furnished rental · 20-year loan",
			pdf: "Generate PDF",
			field: "Estimated resale price",
			fieldBefore: "€160,000",
			fieldAfter: "€146,000",
			reference: "Reference",
			adjusted: "Adjusted",
			horizon: "22 years",
			rows: {
				balance: "Net cash flow",
				resale: "Resale price",
				capital: "Net capital built",
				gain: "Gain",
				irr: "IRR",
			},
			values: {
				reference: {
					balance: "−€67,500",
					resale: "€160,000",
					capital: "€159,300",
					gain: "+€91,800",
					irr: "7.4%",
				},
				adjusted: {
					balance: "−€67,500",
					resale: "€146,000",
					capital: "€145,300",
					gain: "+€77,800",
					irr: "6.6%",
				},
			},
			verdict: "Even sold for less, the investment still comes out ahead.",
			followUp: {
				title: "My alerts",
				subtitle: "Yearly portfolio follow-up",
				count: "3 opportunities",
				upToDate: "Up to date",
				booked: "Meeting proposed",
				clients: {
					a: {
						initials: "D",
						name: "Mr and Mrs Durand",
						project: "Flat · furnished rental",
						alert: "Annual review to present",
					},
					b: {
						initials: "L",
						name: "Ms Lefèvre",
						project: "Residence · Pinel",
						alert: "Tax benefit ends in 6 months",
					},
					c: {
						initials: "G",
						name: "Mr Garnier",
						project: "Building · property deficit",
						alert: "Loan worth renegotiating",
					},
				},
			},
		},
	},
	blog: {
		heroLabel: "Blog",
		heroTitle: "Articles",
		description: "Notes, guides and news.",
		featured: "Latest article",
		latest: "More articles",
		empty: "No articles yet.",
		back: "Back to the blog",
		other: "Other articles",
		errorTitleList: "Unable to load articles",
		errorTitleDetail: "Unable to load this article",
		errorFallback: "Please try again later.",
	},
	docs: {
		heroLabel: "Documents",
		heroTitle: "Public documents",
		heroDescription: "Legal notice, privacy policy and other public documents.",
		empty: "No documents yet.",
		effectiveDate: "Last updated",
		cardDescriptions: {
			legal: "Publisher and hosting information.",
			privacy: "How personal data is handled on this site.",
		},
	},
	consent: {
		title: "Cookies and analytics",
		description:
			"With your consent, PostHog measures pages viewed, traffic sources, contact clicks and form submissions to improve the site.",
		essentialTitle: "Essential",
		essentialDescription:
			"Needed for the site to work, such as remembering your language.",
		analyticsTitle: "Analytics",
		analyticsDescription:
			"Optional audience measurement with PostHog, hosted in the EU. You can decline or withdraw consent in cookie preferences. Your choice is valid for six months.",
		policyLink: "Read the privacy policy",
		showDetails: "Details",
		hideDetails: "Hide details",
		close: "Close",
		deny: "Decline",
		accept: "Accept",
	},
	footer: {
		description:
			"Lezo builds and maintains web and mobile applications, and publishes its own software products.",
		servicesTitle: "Services",
		companyTitle: "Lezo",
		infoTitle: "Information",
		contact: "Contact",
		docs: "Documents",
		legal: "Legal notice",
		privacy: "Privacy",
		cookies: "Cookie preferences",
		rights: "All rights reserved.",
	},
	notFound: {
		label: "Error",
		title: "Page not found",
		description: "The page you are looking for does not exist or has moved.",
		backHome: "Back to home",
	},
};

export type Translations = InferTranslation<typeof en>;
